import { useState } from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import Slide from '@mui/material/Slide';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PaletteIcon from '@mui/icons-material/Palette';
import { useUserContext } from '../../contexts/UserContext';
import NotificationBadge from '../common/NotificationBadge';
import { getUserAvatarUrl, getUserAvatarErrorHandler } from '../../utils/avatarUtils';

interface HideOnScrollProps {
  children: React.ReactElement;
}

function HideOnScroll(props: HideOnScrollProps) {
  const { children } = props;
  const trigger = useScrollTrigger();

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

export default function Header() {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useUserContext();
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);

  const handleSmoothScroll = (elementId: string) => {
    // If not on homepage, navigate first then scroll
    if (window.location.pathname !== '/') {
      navigate('/');
      // Wait for navigation to complete before scrolling
      setTimeout(() => {
        const element = document.getElementById(elementId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      // Already on homepage, just scroll
      const element = document.getElementById(elementId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const handleWalletClick = async (event: React.MouseEvent<HTMLElement>) => {
    if (isAuthenticated) {
      setAnchorEl(event.currentTarget as any);
    } else {
      // Navigate to auth page for connection/registration
      navigate('/auth');
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      handleClose();
      setMobileMenuAnchor(null);
    } catch (error) {
      console.error('Logout failed:', error);
      // Fallback: clear local storage and navigate anyway
      localStorage.removeItem('token');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('authMethod');
      navigate('/');
      handleClose();
      setMobileMenuAnchor(null);
    }
  };

  const menuItems = [
    ...(isAuthenticated ? [
        { label: 'Dashboard', path: '/dashboard', icon: <DashboardIcon sx={{ fontSize: 18 }} /> },
    ] : [])
  ];

  return (
    <HideOnScroll>
      <AppBar 
        position="fixed" 
        sx={{ 
          backdropFilter: 'blur(20px)',
          background: 'rgba(45, 45, 55, 0.35)',
          borderBottom: '1px solid rgba(130, 130, 140, 0.4)',
          boxShadow: '0 4px 24px rgba(0, 0, 0, 0.4)'
        }}
      >
        <Toolbar sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Box
              component={RouterLink}
              to={isAuthenticated ? "/dashboard" : "/"}
              sx={{
                textDecoration: 'none',
                mr: 4,
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.8,
                  transform: 'scale(1.02)',
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              <Box
                component="img"
                src="/spherelogo-header.png"
                alt="Sphere Logo"
                sx={{
                  height: { xs: 32, sm: 32 },
                  width: 'auto',
                  maxWidth: '100%',
                  objectFit: 'contain'
                }}
              />
            </Box>

            {/* Desktop Navigation */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1 }}>
              <Button
                onClick={() => handleSmoothScroll('your-sphere')}
                sx={{
                  color: 'text.primary',
                  textTransform: 'none',
                  fontWeight: 500,
                  px: 2,
                  '&:hover': {
                    bgcolor: 'action.hover',
                    color: 'primary.main'
                  }
                }}
              >
                Features
              </Button>
              <Button
                onClick={() => handleSmoothScroll('tokenomics')}
                sx={{
                  color: 'text.primary',
                  textTransform: 'none',
                  fontWeight: 500,
                  px: 2,
                  '&:hover': {
                    bgcolor: 'action.hover',
                    color: 'primary.main'
                  }
                }}
              >
                Tokenomics
              </Button>
              <Button
                onClick={() => handleSmoothScroll('utility')}
                sx={{
                  color: 'text.primary',
                  textTransform: 'none',
                  fontWeight: 500,
                  px: 2,
                  '&:hover': {
                    bgcolor: 'action.hover',
                    color: 'primary.main'
                  }
                }}
              >
                Utility
              </Button>
              {menuItems.map((item) => (
                <Button
                  key={item.label}
                  component={RouterLink}
                  to={item.path}
                  startIcon={item.icon}
                  sx={{
                    color: 'text.primary',
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 2,
                    '&:hover': {
                      bgcolor: 'action.hover',
                      color: 'primary.main'
                    }
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          </Box>

          {/* Desktop Wallet/User Section */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2 }}>
            {isAuthenticated && user ? (
              <>
                <NotificationBadge />
                <Chip
                  avatar={
                    <Avatar 
                      key={`header-avatar-${user?.id}-${user?.socialCard?.avatarImage || 'default'}`}
                      src={getUserAvatarUrl(user)}
                      onError={getUserAvatarErrorHandler(user)}
                      sx={{ 
                        bgcolor: 'primary.main',
                        cursor: 'pointer',
                        '&:hover': {
                          transform: 'scale(1.05)',
                        },
                        transition: 'transform 0.2s ease'
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(`/@${user.handle}`, '_blank');
                      }}
                    />
                  }
                  label={`@${user.handle}`}
                  onClick={handleWalletClick}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'action.hover'
                    }
                  }}
                />
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                  PaperProps={{
                    sx: {
                      mt: 1.5,
                      minWidth: 200,
                      borderRadius: 1.5,
                      padding: 0.5,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <MenuItem component={RouterLink} to="/dashboard" onClick={handleClose}>
                    <DashboardIcon sx={{ mr: 2, fontSize: 20 }} />
                    Dashboard
                  </MenuItem>
                  <MenuItem component={RouterLink} to="/dashboard/social-card" onClick={handleClose}>
                    <PaletteIcon sx={{ mr: 2, fontSize: 20 }} />
                    Social Card
                  </MenuItem>
{/*                   <MenuItem component={RouterLink} to="/dashboard/marketplace" onClick={handleClose}>
                    <ShoppingCartIcon sx={{ mr: 2, fontSize: 20 }} />
                    Marketplace
                  </MenuItem> */}
                  <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
                    <AccountBalanceWalletIcon sx={{ mr: 2, fontSize: 20 }} />
                    Disconnect
                  </MenuItem>
                </Menu>
              </>
            ) : (
              <Button
                variant="contained"
                startIcon={<AccountBalanceWalletIcon />}
                onClick={handleWalletClick}
                sx={{
                  background: (theme) => `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  boxShadow: '0 2px 10px rgba(99, 102, 241, 0.3)',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  '&:hover': {
                    boxShadow: '0 4px 20px rgba(99, 102, 241, 0.4)',
                    transform: 'translateY(-1px)',
                  }
                }}
              >
                Connect Wallet
              </Button>
            )}
          </Box>

          {/* Mobile Menu Button and Notifications */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1 }}>
            {isAuthenticated && <NotificationBadge />}
            <IconButton
              onClick={(e) => setMobileMenuAnchor(e.currentTarget as any)}
            >
              <MenuIcon />
            </IconButton>
          </Box>

          {/* Mobile Menu */}
          <Menu
            anchorEl={mobileMenuAnchor}
            open={Boolean(mobileMenuAnchor)}
            onClose={() => setMobileMenuAnchor(null)}
            PaperProps={{
              sx: {
                width: '100%',
                maxWidth: 300,
                mt: 1
              }
            }}
          >
            <MenuItem
              onClick={() => {
                setMobileMenuAnchor(null);
                handleSmoothScroll('your-sphere');
              }}
            >
              Features
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMobileMenuAnchor(null);
                handleSmoothScroll('tokenomics');
              }}
            >
              Tokenomics
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMobileMenuAnchor(null);
                handleSmoothScroll('utility');
              }}
            >
              Utility
            </MenuItem>
            {menuItems.map((item) => (
              <MenuItem
                key={item.label}
                component={RouterLink}
                to={item.path}
                onClick={() => setMobileMenuAnchor(null)}
              >
                {item.icon && <Box sx={{ mr: 2 }}>{item.icon}</Box>}
                {item.label}
              </MenuItem>
            ))}
            <MenuItem
              onClick={isAuthenticated ? handleLogout : () => {
                setMobileMenuAnchor(null);
                navigate('/auth');
              }}
              sx={{ 
                mt: 1, 
                pt: 2, 
                            borderTop: '1px solid rgba(255, 255, 255, 0.15)',
                color: isAuthenticated ? 'error.main' : 'primary.main'
              }}
            >
              <AccountBalanceWalletIcon sx={{ mr: 2 }} />
              {isAuthenticated ? 'Disconnect Wallet' : 'Connect Wallet'}
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>
    </HideOnScroll>
  );
} 